import React from 'react';
import { Sparkles, RotateCcw, CircleDollarSign } from 'lucide-react';
import { motion } from 'framer-motion';
import { useWorkout } from '../WorkoutContext';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { useModal } from '../../../contexts/ModalContext';
import { generateFinalWorkout } from '../../../services/handlers/serviceHandler';


const LoadingState = () => (
  <div className="flex flex-col items-center justify-center min-h-[420px]">
    <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin"
      style={{ filter: 'drop-shadow(0 0 12px rgba(248,7,5,0.6))' }} />
    <p className="mt-6 text-gray-700 font-semibold">Channelling your perfect workout plan...</p>
    <p className="mt-1 text-sm text-gray-500">Hang tight — this is going to be electric.</p>
  </div>
);


const DayItem = ({ day, index }) => (
  <div className="flex items-start gap-4 py-3 border-b border-gray-200 last:border-b-0">
    <div className="flex-shrink-0 flex flex-col items-center">
      <span className="text-xs text-gray-500 mb-1 font-semibold tracking-wider">DAY</span>
      <div className="w-10 h-10 rounded-full flex items-center justify-center"
        style={{
          background: 'linear-gradient(180deg, #ff4a4a 0%, #e30b09 60%, #9a0303 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45), 0 4px 10px rgba(200,0,0,0.4)',
          border: '1px solid #4a0000'
        }}
      >
        <span className="aw-text-3d-white font-extrabold text-xl">{index + 1}</span>
      </div>
    </div>
    <div className="flex-1 pt-6">
      <h3 className="font-semibold aw-text-3d-dark">{day.focus}</h3>
    </div>
  </div>
);


const StepSeven = ({ onError, formData, onGenerateFinal, onBack, updateFields }) => {
  const { 
    workoutStrategy, 
    setShowFeedbackHints,
    setPreviousStrategy,
    setWorkoutFinal,
    setHasSaved,
  } = useWorkout();

  const { userProfile, refreshUserProfile } = useUserProfile();
  const { showModal } = useModal();

  if (!workoutStrategy) {
    return <LoadingState />;
  
  }


  console.log('Days:', workoutStrategy.workoutPlan?.days);


  const {
    workoutPlan: {
      days = {},
      workoutPlan: {
        daysPerWeek = 3,
        overallStrategy = '',
        programDuration = '',
        timePerSession = ''
      } = {}
    } = {}
  } = workoutStrategy || {};

  const formattedDays = Object.entries(days)
    .filter(([key]) => key.startsWith('day'))
    .sort(([a], [b]) => {
      const numA = parseInt(a.replace('day', ''));
      const numB = parseInt(b.replace('day', ''));
      return numA - numB;
    })
    .map(([key, day]) => ({
      name: `Day ${key.replace('day', '')}`,
      focus: day.strategy || 'Rest Day',
      muscleGroup: day.muscleGroup
    }));

    const handleReviewStrategy = () => {
      if (workoutStrategy) {
        setPreviousStrategy(workoutStrategy);
        // Update both fields in a single call to ensure atomic update
        updateFields({
          originalAdditionalNotes: formData.additionalNotes,
          additionalNotes: ''
        });
        setShowFeedbackHints(true);
      }
      onBack();
    };

  const handleGenerateFinalWorkout = async () => {
    if (!userProfile?.credits || userProfile.credits < 1) {
      showModal('Error', 'You need at least 1 credit to generate a workout', 'error');
      return;
    }
  
    setWorkoutFinal(null);
    setHasSaved(false);
    onGenerateFinal();
    
    try {
      const workoutFinalPlan = await generateFinalWorkout(workoutStrategy, {
        ...formData,
        email: userProfile.email
      });
      
      await refreshUserProfile();
      
      setWorkoutFinal({
        ...workoutFinalPlan,
        workoutPlan: {
          ...workoutFinalPlan.workoutPlan,
          notes: workoutStrategy.notes || {}
        }
      });
    } catch (err) {
      console.error('Workout generation error:', err);
      if (err.message === 'Insufficient credits') {
        showModal('Error', 'You need credits to generate workouts', 'error');
      } else {
        showModal('Error', 'Failed to generate workout', 'error');
      }
      onBack();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8 w-full max-w-2xl mx-auto p-3"
    >
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-extrabold aw-text-3d-dark">This is the strategy we put together</h2>
        <p className="text-gray-600">Please confirm strategy below or provide feedback to improve it</p>
      </div>

      {/* Stats Display */}
      <div className="grid grid-cols-2 gap-8 my-8">
        <div className="text-center">
          <div className="text-6xl font-extrabold aw-text-3d-red">{daysPerWeek}</div>
          <div className="text-sm font-semibold text-black mt-2 uppercase tracking-wider">days per week</div>
        </div>
        <div className="text-center">
          <div className="text-6xl font-extrabold aw-text-3d-red">{timePerSession}</div>
          <div className="text-sm font-semibold text-black mt-2 uppercase tracking-wider">minutes per session</div>
        </div>
      </div>

      {/* Duration */}
      <div className="text-center">
        <span className="text-gray-600">Plan Duration:</span>
        <span className="ml-2 font-bold aw-text-glow-red">{programDuration}</span>
      </div>

      {/* Strategy */}
      <div className="space-y-2">
        <h3 className="text-xl font-extrabold aw-text-3d-dark p-3">Strategy</h3>
        <p className="text-black/80 leading-relaxed">{overallStrategy}</p>
      </div>

      {/* Weekly Schedule - Updated */}
      <div className="space-y-4">
        <div className="flex items-center">
          <h3 className="text-xl font-extrabold aw-text-3d-dark">Weekly Schedule</h3>
        </div>

        <div className="rounded-xl px-4"
          style={{
            background: 'linear-gradient(180deg, #fafafa 0%, #f0f0f3 100%)',
            border: '1px solid #e0e0e4',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)'
          }}
        >
          {formattedDays.map((day, index) => (
            <DayItem key={index} day={day} index={index} />
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-4">
        <button
          onClick={handleGenerateFinalWorkout}
          className="aw-btn-3d aw-btn-generate w-full py-5 rounded-2xl font-extrabold text-lg uppercase
            flex items-center justify-center gap-3"
        >
          <Sparkles className="w-6 h-6" />
          <span className="aw-text-3d-white">Generate Final Workout</span>
          <div className="flex items-center gap-1 ml-1 px-2 py-1 rounded-lg"
            style={{
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(0,0,0,0.5)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15)'
            }}
          >
            <CircleDollarSign className="w-4 h-4" />
            <span className="text-sm aw-text-3d-white">1</span>
          </div>
        </button>

        <button
          onClick={handleReviewStrategy}
          className="aw-btn-3d aw-btn-light w-full py-3 rounded-xl font-semibold
            flex items-center justify-center gap-2 text-black"
        >
          <RotateCcw className="w-5 h-5" />
          Review Strategy
        </button>
      </div>
    </motion.div>
  );
};

export default StepSeven;